import { Plus, X } from 'lucide-react';
import type { VlanRange } from '@/hooks/useDeviceActions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface VlanRangeListInputProps {
  value: VlanRange[];
  onChange: (ranges: VlanRange[]) => void;
  disabled?: boolean;
  idPrefix?: string;
}

/**
 * Editor for a `vlan_range_list` operation variable — a list of VLAN ranges.
 * Each row is a start/end pair (leave End equal to Start for a single VLAN).
 * The value is vendor-neutral; the gateway renders it per-platform (Cisco
 * `1-6,10`, Huawei `1 to 6 10`, Junos `[1-6 10]`).
 */
export function VlanRangeListInput({ value, onChange, disabled, idPrefix = 'vlan-range' }: VlanRangeListInputProps) {
  const update = (i: number, patch: Partial<VlanRange>) => {
    onChange(value.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const addRow = () => {
    onChange([...value, { start: 1, end: 1 }]);
  };
  const removeRow = (i: number) => {
    onChange(value.filter((_, idx) => idx !== i));
  };

  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-xs text-muted-foreground">No VLANs yet — add a range below.</p>
      ) : (
        value.map((range, i) => {
          const invalid = range.start > range.end || range.start < 1 || range.end > 4094;
          return (
            <div key={i} className="flex items-center gap-2">
              <Input
                id={`${idPrefix}-start-${String(i)}`}
                type="number"
                min={1}
                max={4094}
                value={Number.isFinite(range.start) ? range.start : ''}
                onChange={(e) => { update(i, { start: e.target.valueAsNumber }); }}
                disabled={disabled}
                aria-label={`Range ${String(i + 1)} start VLAN`}
                className="w-24 font-mono"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                id={`${idPrefix}-end-${String(i)}`}
                type="number"
                min={1}
                max={4094}
                value={Number.isFinite(range.end) ? range.end : ''}
                onChange={(e) => { update(i, { end: e.target.valueAsNumber }); }}
                disabled={disabled}
                aria-label={`Range ${String(i + 1)} end VLAN`}
                className={cnInvalid(invalid)}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => { removeRow(i); }}
                disabled={disabled}
                aria-label={`Remove range ${String(i + 1)}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          );
        })
      )}
      <Button type="button" variant="outline" size="sm" onClick={addRow} disabled={disabled}>
        <Plus className="w-3.5 h-3.5" />
        Add range
      </Button>
    </div>
  );
}

function cnInvalid(invalid: boolean): string {
  return invalid ? 'w-24 font-mono border-destructive' : 'w-24 font-mono';
}
